/**
 * Indicator — small status pill that signals "something is here" on a
 * host element. Architecturally a quieter sibling of Badge: container-
 * tinted (never the role's main hue at full strength) so a corner mark
 * never dominates the host. Two content modes:
 *
 *   - `dot`   — empty circular pill, no text. Use for binary signals
 *               (online / unread / new content).
 *   - `count` — pill with a number (or short label). Default when
 *               `count` or `label` is set.
 *
 * Indicator is intentionally agnostic about WHERE it's mounted. To pin
 * it to a corner of a host element, wrap the host in `<IndicatorAnchor>`.
 *
 * Sizes (matches Figma — Tiny / Small / Normal):
 *   - `s` — 16 px, label.s — counter-in-button
 *   - `m` — 20 px, label.m — DEFAULT
 *   - `l` — 24 px, label.l — large host corners
 *
 * Brightness (matches Figma):
 *   - `bright` — bg = `palette.{tone}.container`, fg = `onContainer`
 *                (neutral → `inverseSurface` / `inverseOnSurface`).
 *                Default.
 *   - `gentle` — bg = `palette.{tone}.container` at 50% alpha, fg =
 *                `palette.{tone}.main` (neutral → translucent surface3
 *                with `onSurface` text).
 *
 * @format
 */

import {forwardRef, type CSSProperties, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled, alpha} from '@mui/material/styles';

import {text, type TypographyRole} from 'ui/theme/typography';

export const INDICATOR_KINDS = ['dot', 'count'] as const;
export type IndicatorKind = (typeof INDICATOR_KINDS)[number];

export const INDICATOR_SIZES = ['nano', 'micro', 'mini'] as const;
export type IndicatorSize = (typeof INDICATOR_SIZES)[number];

export const INDICATOR_TONES = ['primary', 'good', 'bad', 'alert', 'neutral'] as const;
export type IndicatorTone = (typeof INDICATOR_TONES)[number];

export const INDICATOR_BRIGHTNESS = ['bright', 'gentle'] as const;
export type IndicatorBrightness = (typeof INDICATOR_BRIGHTNESS)[number];

interface SizeGeometry {
  height: number;
  padX: number;
  labelRole: TypographyRole;
}

const GEO: Record<IndicatorSize, SizeGeometry> = {
  nano: {height: 16, padX: 4, labelRole: 'label.s'},
  micro: {height: 20, padX: 4, labelRole: 'label.m'},
  mini: {height: 24, padX: 2, labelRole: 'label.l'},
};

interface IndicatorPalette {
  bg: string;
  fg: string;
}

function paletteFor(
  theme: import('@mui/material/styles').Theme,
  brightness: IndicatorBrightness,
  tone: IndicatorTone,
): IndicatorPalette {
  const p = theme.palette;

  if (tone === 'neutral') {
    if (brightness === 'bright') {
      return {bg: p.surface.inverseSurface, fg: p.surface.inverseOnSurface};
    }
    return {bg: alpha(p.surface.surface3, 0.5), fg: p.surface.onSurface};
  }

  const role = p[tone];
  if (brightness === 'bright') {
    return {bg: role.container, fg: role.onContainer};
  }
  return {bg: alpha(role.container, 0.5), fg: role.main};
}

const Pill = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: IndicatorSize;
  $brightness: IndicatorBrightness;
  $tone: IndicatorTone;
  $isDot: boolean;
}>(({theme, $size, $brightness, $tone, $isDot}) => {
  const g = GEO[$size];
  const p = paletteFor(theme, $brightness, $tone);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: g.height,
    height: g.height,
    padding: $isDot ? 0 : `0 ${g.padX}px`,
    borderRadius: g.height / 2,
    backgroundColor: p.bg,
    color: p.fg,
    boxSizing: 'border-box',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    ...text(g.labelRole),
    lineHeight: 1,
  };
});

export interface IndicatorProps {
  kind?: IndicatorKind;
  count?: number;
  max?: number;
  label?: string;
  size?: IndicatorSize;
  tone?: IndicatorTone;
  brightness?: IndicatorBrightness;
  ariaLabel?: string;
  className?: string;
  style?: CSSProperties;
}

function resolveContent(
  count: number | undefined,
  max: number,
  label: string | undefined,
): ReactNode {
  if (label !== undefined) return label;
  if (count === undefined) return null;
  if (count > max) return `${max}+`;
  return String(count);
}

export const Indicator = forwardRef<HTMLDivElement, IndicatorProps>(function Indicator(
  {
    kind,
    count,
    max = 99,
    label,
    size = 'micro',
    tone = 'primary',
    brightness = 'bright',
    ariaLabel,
    className,
    style,
  },
  ref,
) {
  const hasContent = count !== undefined || label !== undefined;
  const resolvedKind: IndicatorKind = kind ?? (hasContent ? 'count' : 'dot');

  const content = resolvedKind === 'dot' ? null : resolveContent(count, max, label);
  const aria =
    ariaLabel ??
    (label ? label : count !== undefined ? `${count > max ? `${max}+` : count}` : undefined);

  return (
    <Pill
      ref={ref}
      role='status'
      aria-label={aria}
      $size={size}
      $brightness={brightness}
      $tone={tone}
      $isDot={resolvedKind === 'dot'}
      className={className}
      style={style}
    >
      {content}
    </Pill>
  );
});
